import React from "react";
import { Box, Typography, Divider, Alert, Link } from "@mui/material";
import AppModal from "../common/AppModal";
import DetailItem from "../common/DetailItem";
import AppCircularProgress from "../common/AppCircularProgress";
import { SupplierResponseDto } from "../../shared/dtos/supplier/SupplierResponseDto";

interface ViewSupplierModalProps {
  open: boolean;
  onClose: () => void;
  supplier: SupplierResponseDto | null;
  loading: boolean;
  error?: string | null;
}

const ViewSupplierModal: React.FC<ViewSupplierModalProps> = ({
  open,
  onClose,
  supplier,
  loading,
  error = null,
}) => {
  const renderContent = (): React.ReactNode => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            my: 5,
          }}
        >
          <AppCircularProgress />
          <Typography sx={{ ml: 2, color: "text.secondary" }}>
            Loading Details...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!supplier) {
      return (
        <Typography sx={{ my: 2, color: "text.secondary" }}>
          Data not available.
        </Typography>
      );
    }

    return (
      <Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <DetailItem label="Company Name" value={supplier.name} />
          <DetailItem
            label="Category"
            value={supplier.productCategory || "N/A"}
          />
          <DetailItem label="Tax ID" value={supplier.taxId || "N/A"} />
          <DetailItem
            label="Website"
            value={
              supplier.website ? (
                <Link
                  href={supplier.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="info"
                  sx={{ textDecoration: "underline" }}
                >
                  {supplier.website}
                </Link>
              ) : (
                "N/A"
              )
            }
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          Contact Information
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <DetailItem
            label="Contact Person"
            value={supplier.contactPerson || "N/A"}
          />
          <DetailItem label="Email" value={supplier.email} />
          <DetailItem label="Phone" value={supplier.phoneNumber} />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          Address Details
        </Typography>

        {supplier.address ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <DetailItem label="Street 1" value={supplier.address.streetLine1} />
            <DetailItem
              label="Street 2"
              value={supplier.address.streetLine2 || "N/A"}
            />
            <DetailItem label="City" value={supplier.address.city} />
            <DetailItem
              label="State"
              value={supplier.address.stateOrProvince}
            />
            <DetailItem
              label="Postal Code"
              value={supplier.address.postalCode}
            />
            <DetailItem label="Country" value={supplier.address.country} />
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No address on file.
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <AppModal open={open} onClose={onClose} title="Supplier Details" width="md">
      {renderContent()}
    </AppModal>
  );
};

export default ViewSupplierModal;
